import { useMemo, useState, type ReactNode } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Badge,
  Drawer,
  Divider,
  Collapse,
  Autocomplete,
  TextField,
  Skeleton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Groups as GroupsIcon,
  FilterAltOutlined as FilterAltOutlinedIcon,
  Close as CloseIcon,
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowRight as CollapsedIcon,
} from '@mui/icons-material'
import { Contact, TargetCompanyTier } from '../../services/api'
import {
  CONNECTION_TYPE_OPTIONS,
  CONTACT_STATUS_OPTIONS,
  CONTACT_STATUS_UNSET_LABEL,
  DEFAULT_CONNECTION_TYPE,
  formatReachOutAt,
} from '../../constants/networking'
import { getEnhancedTableStyles } from '../../utils/tableStyles'
import { tabularNums } from '../../theme'
import { companyGroupKey, companyGroupLabels, normalizeCompanyName } from '../../utils/companyName'
import SortableTableHeader, { SortOrder } from '../common/table/SortableTableHeader'
import { useTablePagination } from '../common/table/useTablePagination'
import TablePaginationBar from '../common/table/TablePaginationBar'
import ContactCard from './ContactCard'
import AssignmentAgeNote from './AssignmentAgeNote'
import { assignmentAge } from '../../utils/contactAssignment'

// The four tiers staff rank a listed company at, plus 'unlisted' for a company
// that is not on the client's list at all (MWA-420).
type ContactCategory = TargetCompanyTier | 'unlisted'

const CATEGORY_TABS: { key: ContactCategory; label: string; shortLabel: string }[] = [
  { key: 'dream', label: 'Dream Companies', shortLabel: 'Dream' },
  { key: 'target', label: 'Target Companies', shortLabel: 'Target' },
  { key: 'backup', label: 'Back-Up Companies', shortLabel: 'Back-Up' },
  { key: 'wildcard', label: 'Wildcard Companies', shortLabel: 'Wildcard' },
  { key: 'unlisted', label: 'Unlisted Companies', shortLabel: 'Unlisted' },
]

const STATUS_LABELS: Record<string, string> = Object.fromEntries(
  CONTACT_STATUS_OPTIONS.map((s) => [s.value, s.label])
)
const STATUS_CHIP_COLORS: Record<
  string,
  'default' | 'info' | 'primary' | 'success' | 'warning' | 'error'
> =
  Object.fromEntries(CONTACT_STATUS_OPTIONS.map((s) => [s.value, s.chipColor]))
const CONNECTION_LABELS: Record<string, string> = Object.fromEntries(
  CONNECTION_TYPE_OPTIONS.map((c) => [c.value, c.label])
)

// Older imports carry statuses and connection types that are no longer in the
// option lists. Show what the record actually says, humanised — labelling an
// unrecognised status as "Not Contacted" (what the old contact rows did) states
// something untrue about the contact.
const humanise = (raw: string) => raw.replace(/_/g, ' ')
// An absent status is its own state — nobody has reached out yet — and reads as
// such rather than being blanked or guessed at.
const statusLabel = (status?: string) =>
  status ? STATUS_LABELS[status] || humanise(status) : CONTACT_STATUS_UNSET_LABEL
// A contact with no connection type reads as the default level, which is the
// same fallback the detail panel applies. The migration deliberately leaves the
// field absent rather than inventing a level, so the two surfaces have to agree
// on what absent means — they previously did not, and the same contact showed
// "1st Level" in the panel and a blank in the row.
const connectionLabel = (connectionType?: string) =>
  connectionType
    ? CONNECTION_LABELS[connectionType] || humanise(connectionType)
    : CONNECTION_LABELS[DEFAULT_CONNECTION_TYPE]

interface NetworkingContactsTableProps {
  /** Contacts already narrowed by the caller's search / status / priority row. */
  contacts: Contact[]
  /** Lowercased company name to target tier, from the client's dream-job data. */
  targetCompanyMap: Record<string, TargetCompanyTier>
  onContactClick: (contact: Contact) => void
  /**
   * Staff-only. The priority flag is a coaching action: update-contact refuses
   * it for portal clients, so the toggle is simply absent for them and the
   * column shows the flag read-only instead.
   */
  onPriorityToggle?: (contact: Contact) => void
  /** Whose empty states to write — staff read about "this client", clients about themselves. */
  audience: 'staff' | 'client'
  /** True when the caller's own filter row is narrowing the list. */
  hasActiveFilters?: boolean
  /**
   * The caller's own filter controls (search, status, priority-only), rendered
   * inside this component's filter panel so every filter on the page sits in
   * one place — and, on a phone, inside one sheet. The caller keeps the state;
   * only the rendering moves here.
   */
  filterSlot?: ReactNode
  /** Clears the caller's filters. Paired with this component's own company filter. */
  onClearFilters?: () => void
  /** Contacts before the caller's filters, for the "N of M" result line. */
  totalCount?: number
  /** Priority-flagged contacts in the whole network, shown beside the count. */
  priorityCount?: number
  /** Renders the table's own shape while the caller is still loading. */
  loading?: boolean
  /**
   * Staff only (MWA-354). When set, a contact awaiting outreach shows how long
   * it has waited since assignment, flagged overdue past this many days. The
   * portal leaves it unset, so its rows render exactly as before.
   */
  assignmentOverdueDays?: number
}

/**
 * The networking contact list, as a table with a tab per company category.
 *
 * Replaces the previous three-level accordion stack (tier accordion wrapping a
 * per-company accordion wrapping contact rows), which buried every contact
 * behind two clicks and gave no way to sort or page. Shared by the staff
 * Networking page and the client portal so both surfaces stay identical apart
 * from the priority toggle.
 *
 * Three renderings of the same data, and every one of them carries every field:
 * the full table from 1200px, the same table with Title, Connection and Reach
 * Out behind a row expander below that, and a card list below 768px where no
 * arrangement of seven columns is readable.
 */
export default function NetworkingContactsTable({
  contacts,
  targetCompanyMap,
  onContactClick,
  onPriorityToggle,
  audience,
  hasActiveFilters = false,
  filterSlot,
  onClearFilters,
  totalCount,
  priorityCount,
  loading = false,
  assignmentOverdueDays,
}: NetworkingContactsTableProps) {
  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  const isPhone = useMediaQuery(theme.breakpoints.down(768))
  const isTablet = useMediaQuery(theme.breakpoints.down(1200))

  const [activeCategory, setActiveCategory] = useState<ContactCategory>('dream')
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<SortOrder>('asc')
  const [companyFilter, setCompanyFilter] = useState<string | null>(null)
  const [filterSheetOpen, setFilterSheetOpen] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const canTogglePriority = !!onPriorityToggle

  // One clock per list, so every row in a render ages against the same moment.
  const now = useMemo(() => new Date(), [contacts])
  const ageOf = (contact: Contact) =>
    assignmentOverdueDays == null ? null : assignmentAge(contact, assignmentOverdueDays, now)

  // Keyed the same way the map is built, and the same way the server matches:
  // a legal suffix must not split one employer into two tiers.
  // A company absent from the map is not on the client's list at all.
  const categoryOf = (contact: Contact): ContactCategory =>
    targetCompanyMap[normalizeCompanyName(contact.company)] || 'unlisted'

  const contactsByCategory = useMemo(() => {
    const grouped: Record<ContactCategory, Contact[]> = { dream: [], target: [], backup: [], wildcard: [], unlisted: [] }
    for (const c of contacts) grouped[categoryOf(c)].push(c)
    return grouped
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contacts, targetCompanyMap])

  // Whether the client has any company set at this tier at all — an empty Dream
  // tab means something different when no dream companies have been chosen yet.
  // Unlisted is never set: it is whatever is left off the list.
  const tierIsDefined = useMemo(() => {
    const tiers = Object.values(targetCompanyMap)
    return {
      dream: tiers.includes('dream'),
      target: tiers.includes('target'),
      backup: tiers.includes('backup'),
      wildcard: tiers.includes('wildcard'),
      unlisted: true,
    }
  }, [targetCompanyMap])

  const rows = contactsByCategory[activeCategory]

  const companyKeyOf = (contact: Contact) => companyGroupKey(contact.company || 'Other')

  // Labels come from every contact, not just this tab, so a filter carried
  // across a tab switch still reads as the company rather than its raw key.
  const companyLabels = useMemo(
    () => companyGroupLabels(contacts.map((c) => c.company || 'Other')),
    [contacts]
  )

  // Company typeahead, scoped to the active tab. The per-company accordions are
  // gone, so this is how you narrow to one company. Options are group keys, so
  // "razor" and "Razor" are one option.
  const companyOptions = useMemo(() => {
    const keys = new Set<string>()
    for (const c of rows) keys.add(companyKeyOf(c))
    return Array.from(keys).sort((a, b) =>
      (companyLabels.get(a) ?? a).localeCompare(companyLabels.get(b) ?? b)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, companyLabels])

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  const displayRows = useMemo(() => {
    const filtered = companyFilter
      ? rows.filter((c) => companyKeyOf(c) === companyFilter)
      : rows

    if (!orderBy) {
      return filtered.slice().sort((a, b) => a.fullName.localeCompare(b.fullName))
    }

    return filtered.slice().sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (orderBy) {
        case 'name':
          aVal = a.fullName || ''
          bVal = b.fullName || ''
          break
        case 'company':
          aVal = a.company || ''
          bVal = b.company || ''
          break
        case 'position':
          aVal = a.position || ''
          bVal = b.position || ''
          break
        case 'status':
          aVal = statusLabel(a.status)
          bVal = statusLabel(b.status)
          break
        case 'connection':
          aVal = connectionLabel(a.connectionType)
          bVal = connectionLabel(b.connectionType)
          break
        case 'reachOut':
          // Raw ISO strings, which compare chronologically. Contacts with no
          // instruction carry '' and group together at one end instead of
          // being scattered through the ordering.
          aVal = a.reachOutAt || ''
          bVal = b.reachOutAt || ''
          break
        case 'priority':
          aVal = a.isPriority ? 1 : 0
          bVal = b.isPriority ? 1 : 0
          break
        default:
          return 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return order === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number)
    })
  }, [rows, companyFilter, orderBy, order])

  const { paginated: pagedRows, ...pageCtl } = useTablePagination(
    displayRows,
    `${activeCategory}|${orderBy}|${order}|${companyFilter || ''}`
  )

  const anyFilterActive = hasActiveFilters || !!companyFilter
  const activeFilterCount = (hasActiveFilters ? 1 : 0) + (companyFilter ? 1 : 0)

  const clearEverything = () => {
    setCompanyFilter(null)
    onClearFilters?.()
  }

  // Why this tier is empty, which is a different sentence from "your filters
  // matched nothing" — that case is handled before this is ever called.
  const emptyMessage = () => {
    if (activeCategory === 'dream' || activeCategory === 'target') {
      if (!tierIsDefined[activeCategory]) {
        return audience === 'staff'
          ? `No ${activeCategory} companies have been set for this client yet.`
          : `No ${activeCategory} companies have been set yet.`
      }
      return audience === 'staff'
        ? `No connections at ${activeCategory} companies yet.`
        : `No connections at your ${activeCategory} companies yet. Your strategist has been notified.`
    }
    if (activeCategory === 'backup') {
      if (!tierIsDefined.backup) {
        return audience === 'staff'
          ? 'No back-up companies have been set for this client yet.'
          : 'No back-up companies have been set yet.'
      }
      return 'No connections at back-up companies yet.'
    }
    if (activeCategory === 'wildcard') {
      if (!tierIsDefined.wildcard) {
        return audience === 'staff'
          ? 'No wildcard companies have been set for this client yet.'
          : 'No wildcard companies have been set yet.'
      }
      return 'No connections at wildcard companies yet.'
    }
    return audience === 'staff'
      ? 'No contacts at companies outside this client’s list.'
      : 'No contacts at companies outside your list.'
  }

  const renderPriority = (contact: Contact) => {
    if (canTogglePriority) {
      return (
        <Tooltip title={contact.isPriority ? 'Remove priority' : 'Mark as priority'}>
          <IconButton
            size="small"
            aria-label={
              contact.isPriority
                ? `Remove priority from ${contact.fullName}`
                : `Mark ${contact.fullName} as priority`
            }
            onClick={(e) => {
              // The row itself opens the contact, so keep the two apart.
              e.stopPropagation()
              onPriorityToggle?.(contact)
            }}
            sx={{ color: contact.isPriority ? 'warning.main' : 'action.disabled' }}
          >
            {contact.isPriority ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      )
    }
    return contact.isPriority ? (
      <StarIcon fontSize="small" titleAccess="Priority contact" sx={{ color: 'warning.main' }} />
    ) : (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const companyControl = (
    <Autocomplete
      size={isPhone ? 'medium' : 'small'}
      options={companyOptions}
      value={companyFilter}
      onChange={(_, next) => setCompanyFilter(next)}
      getOptionLabel={(key) => companyLabels.get(key) ?? key}
      renderInput={(params) => (
        <TextField {...params} label="Company" placeholder="Any company" />
      )}
      sx={isPhone ? { width: '100%' } : { minWidth: 180, maxWidth: 240, flex: '1 1 auto' }}
    />
  )

  const resultLine = (
    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
      {anyFilterActive && totalCount != null ? (
        <>
          <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color: 'text.primary' }}>
            {displayRows.length}
          </Box>
          of
          <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color: 'text.primary' }}>
            {totalCount}
          </Box>
          {totalCount === 1 ? 'contact' : 'contacts'}
        </>
      ) : (
        <>
          <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color: 'text.primary' }}>
            {displayRows.length}
          </Box>
          {displayRows.length === 1 ? 'contact' : 'contacts'}
        </>
      )}
      {/* The priority tally the toolbar used to carry as a chip. It counts the
          whole network, not the filtered view, which is why it is separated
          rather than folded into the sentence above. */}
      {!!priorityCount && (
        <>
          <Box component="span" sx={{ color: 'divider' }}>
            ·
          </Box>
          <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color: 'text.primary' }}>
            {priorityCount}
          </Box>
          priority
        </>
      )}
    </Typography>
  )

  const filterPanel = isPhone ? (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Badge badgeContent={activeFilterCount} color="primary">
          <Button
            variant="outlined"
            startIcon={<FilterAltOutlinedIcon />}
            onClick={() => setFilterSheetOpen(true)}
            sx={{ minHeight: 44 }}
          >
            Filters
          </Button>
        </Badge>
        {resultLine}
      </Box>
      <Drawer
        anchor="bottom"
        open={filterSheetOpen}
        onClose={() => setFilterSheetOpen(false)}
        PaperProps={{
          // MuiDrawer's theme override paints paper gray900 for the navigation
          // rail. A content sheet that inherits it renders unreadable, which is
          // what MWA-253's defects were.
          sx: {
            backgroundColor: 'background.paper',
            color: 'text.primary',
            borderRadius: '18px 18px 0 0',
            maxHeight: '85vh',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 2, pb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
            Filters
          </Typography>
          <IconButton
            aria-label="Close filters"
            onClick={() => setFilterSheetOpen(false)}
            sx={{ width: 44, height: 44 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box sx={{ px: 2, py: 2, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filterSlot}
          {companyControl}
        </Box>
        <Divider />
        <Box sx={{ display: 'flex', gap: 1, p: 2 }}>
          <Button
            variant="outlined"
            onClick={clearEverything}
            disabled={!anyFilterActive}
            sx={{ flexGrow: 1, minHeight: 44 }}
          >
            Clear all
          </Button>
          <Button
            variant="contained"
            onClick={() => setFilterSheetOpen(false)}
            sx={{ flexGrow: 2, minHeight: 44 }}
          >
            Show {displayRows.length} {displayRows.length === 1 ? 'result' : 'results'}
          </Button>
        </Box>
      </Drawer>
    </>
  ) : (
    <>
      <Box
        sx={{
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '12px',
          px: 2,
          py: 1.5,
          mb: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        {filterSlot}
        {companyControl}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        {resultLine}
        {/* Not when the list is empty: the empty panel below carries this same
            control, and two identical buttons on one screen make the reader
            check whether they do different things. */}
        {anyFilterActive && displayRows.length > 0 && (
          <Button size="small" onClick={clearEverything}>
            Clear all filters
          </Button>
        )}
      </Box>
    </>
  )

  const tabs = (
    <Tabs
      value={activeCategory}
      onChange={(_, v) => {
        setActiveCategory(v as ContactCategory)
        setExpandedId(null)
      }}
      variant="scrollable"
      scrollButtons="auto"
      sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
    >
      {CATEGORY_TABS.map((t) => (
        <Tab
          key={t.key}
          value={t.key}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {isPhone ? t.shortLabel : t.label}
              <Chip
                label={contactsByCategory[t.key].length}
                size="small"
                sx={{ height: 18, fontSize: '0.7rem', ...tabularNums }}
              />
            </Box>
          }
        />
      ))}
    </Tabs>
  )

  if (loading) {
    return (
      <Box>
        {tabs}
        <Skeleton variant="rounded" height={68} sx={{ mb: 1.5, borderRadius: '12px' }} />
        <Paper variant="outlined" sx={tableStyles.tableContainer}>
          {isPhone ? (
            <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} variant="rounded" height={132} sx={{ borderRadius: '12px' }} />
              ))}
            </Box>
          ) : (
            <Table sx={tableStyles.table}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Company</TableCell>
                  {!isTablet && <TableCell>Title</TableCell>}
                  <TableCell>Status</TableCell>
                  {!isTablet && <TableCell>Connection</TableCell>}
                  {!isTablet && <TableCell>Assigned</TableCell>}
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[0, 1, 2, 3, 4].map((i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton width="70%" /></TableCell>
                    <TableCell><Skeleton width="55%" /></TableCell>
                    {!isTablet && <TableCell><Skeleton width="80%" /></TableCell>}
                    <TableCell><Skeleton variant="rounded" width={84} height={20} /></TableCell>
                    {!isTablet && <TableCell><Skeleton variant="rounded" width={70} height={20} /></TableCell>}
                    {!isTablet && <TableCell><Skeleton width="65%" /></TableCell>}
                    <TableCell><Skeleton variant="circular" width={20} height={20} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Box>
    )
  }

  const listBody = () => {
    if (displayRows.length === 0) {
      // Two different sentences, because they call for two different actions.
      if (anyFilterActive) {
        return (
          <Paper variant="outlined" sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              No contacts match these filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Box component="span" sx={{ ...tabularNums, fontWeight: 600, color: 'text.primary' }}>
                {totalCount ?? contacts.length}
              </Box>{' '}
              {audience === 'staff' ? 'contacts are in this network.' : 'contacts are in your network.'} None of
              them match every filter you have set.
            </Typography>
            <Button variant="outlined" onClick={clearEverything} sx={{ minHeight: 44 }}>
              Clear all filters
            </Button>
          </Paper>
        )
      }
      return (
        <Paper variant="outlined" sx={{ p: { xs: 4, sm: 6 }, textAlign: 'center' }}>
          <GroupsIcon sx={{ fontSize: 48, color: 'action.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {emptyMessage()}
          </Typography>
        </Paper>
      )
    }

    if (isPhone) {
      return (
        <Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {pagedRows.map((c) => (
              <ContactCard
                key={c.id}
                contact={c}
                statusLabel={statusLabel(c.status)}
                statusColor={(c.status && STATUS_CHIP_COLORS[c.status]) || 'default'}
                connectionLabel={connectionLabel(c.connectionType)}
                reachOutLabel={formatReachOutAt(c.reachOutAt) || null}
                onClick={() => onContactClick(c)}
                onPriorityToggle={onPriorityToggle ? () => onPriorityToggle(c) : undefined}
                assignmentAge={ageOf(c)}
              />
            ))}
          </Box>
          <TablePaginationBar
            count={pageCtl.count}
            page={pageCtl.page}
            rowsPerPage={pageCtl.rowsPerPage}
            setPage={pageCtl.setPage}
            setRowsPerPage={pageCtl.setRowsPerPage}
          />
        </Box>
      )
    }

    return (
      <Paper variant="outlined" sx={tableStyles.tableContainer}>
        <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
          <Table stickyHeader sx={tableStyles.table}>
            <TableHead>
              <TableRow>
                {/* The expander column has no label to sort by; it exists only
                    below 1200px, where three columns live behind it. */}
                {isTablet && <TableCell sx={{ width: 44 }} />}
                <SortableTableHeader id="name" label="Name" orderBy={orderBy} order={order} onSort={handleSort} />
                <SortableTableHeader id="company" label="Company" orderBy={orderBy} order={order} onSort={handleSort} />
                {!isTablet && (
                  <SortableTableHeader id="position" label="Title" orderBy={orderBy} order={order} onSort={handleSort} />
                )}
                <SortableTableHeader id="status" label="Status" orderBy={orderBy} order={order} onSort={handleSort} />
                {!isTablet && (
                  <SortableTableHeader id="connection" label="Connection" orderBy={orderBy} order={order} onSort={handleSort} />
                )}
                {!isTablet && (
                  <SortableTableHeader id="reachOut" label="Assigned" orderBy={orderBy} order={order} onSort={handleSort} />
                )}
                <SortableTableHeader id="priority" label="Priority" orderBy={orderBy} order={order} onSort={handleSort} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedRows.map((c) => {
                const isExpanded = expandedId === c.id
                const rowAge = ageOf(c)
                return [
                  <TableRow key={c.id} hover onClick={() => onContactClick(c)} sx={{ cursor: 'pointer' }}>
                    {isTablet && (
                      <TableCell sx={{ pr: 0 }}>
                        <IconButton
                          size="small"
                          aria-label={
                            isExpanded ? `Hide details for ${c.fullName}` : `Show details for ${c.fullName}`
                          }
                          aria-expanded={isExpanded}
                          onClick={(e) => {
                            // The row opens the contact; this only unfolds it.
                            e.stopPropagation()
                            setExpandedId(isExpanded ? null : c.id)
                          }}
                        >
                          {isExpanded ? <ExpandIcon fontSize="small" /> : <CollapsedIcon fontSize="small" />}
                        </IconButton>
                      </TableCell>
                    )}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {c.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{c.company || '—'}</Typography>
                    </TableCell>
                    {!isTablet && (
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {c.position || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip
                        label={statusLabel(c.status)}
                        size="small"
                        color={(c.status && STATUS_CHIP_COLORS[c.status]) || 'default'}
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    {!isTablet && (
                      <TableCell>
                        <Chip
                          label={connectionLabel(c.connectionType)}
                          size="small"
                          variant="outlined"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                    )}
                    {/* When the strategist assigned this contact to the client,
                        not a deadline for the client to act by (MWA-353).
                        Read-only for both audiences — it is authored in the
                        detail panel, and update-contact refuses it from a
                        client. */}
                    {!isTablet && (
                      <TableCell>
                        {formatReachOutAt(c.reachOutAt) ? (
                          <>
                            <Typography variant="body2" sx={tabularNums}>
                              {formatReachOutAt(c.reachOutAt)}
                            </Typography>
                            {rowAge && <AssignmentAgeNote age={rowAge} />}
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>{renderPriority(c)}</TableCell>
                  </TableRow>,
                  isTablet ? (
                    <TableRow key={`${c.id}-details`}>
                      <TableCell colSpan={5} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                        <Collapse in={isExpanded} unmountOnExit>
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                              gap: 1.5,
                              py: 1.5,
                              pl: 3.5,
                            }}
                          >
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Title
                              </Typography>
                              <Typography variant="body2">{c.position || '—'}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
                                Connection
                              </Typography>
                              <Chip
                                label={connectionLabel(c.connectionType)}
                                size="small"
                                variant="outlined"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Assigned
                              </Typography>
                              <Typography variant="body2" sx={tabularNums}>
                                {formatReachOutAt(c.reachOutAt) || '—'}
                              </Typography>
                              {rowAge && <AssignmentAgeNote age={rowAge} />}
                            </Box>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  ) : null,
                ]
              })}
            </TableBody>
          </Table>
        </Box>
        <TablePaginationBar
          count={pageCtl.count}
          page={pageCtl.page}
          rowsPerPage={pageCtl.rowsPerPage}
          setPage={pageCtl.setPage}
          setRowsPerPage={pageCtl.setRowsPerPage}
        />
      </Paper>
    )
  }

  return (
    <Box>
      {tabs}
      {filterPanel}
      {listBody()}
    </Box>
  )
}
